/**
 * @description
 * Sentry configuration for error monitoring and performance tracking.
 * Provides comprehensive error reporting for the admin dashboard.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error monitoring
 */
export function initSentry() {
  // Only initialize in production or when SENTRY_DSN is provided
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!sentryDsn) {
    console.log('Sentry DSN not provided, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    
    // Error Sampling
    sampleRate: 1.0,
    
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',
    
    // User context
    beforeSend(event) {
      // Filter out non-critical errors in development
      if (import.meta.env.MODE === 'development') {
        const error = event.exception?.values?.[0];
        if (error?.type === 'ChunkLoadError' || error?.type === 'Loading chunk failed') {
          return null; // Don't send chunk load errors in development
        }
      }
      return event;
    },
    
    // Additional context
    beforeBreadcrumb(breadcrumb) {
      // Filter out sensitive data
      if (breadcrumb.category === 'fetch' && breadcrumb.data?.url) {
        const url = breadcrumb.data.url as string;
        if (url.includes('/api/auth') || url.includes('/api/stripe')) {
          breadcrumb.data.url = url.replace(/\/api\/auth\/.*/, '/api/auth/***');
          breadcrumb.data.url = breadcrumb.data.url.replace(/\/api\/stripe\/.*/, '/api/stripe/***');
        }
      }
      return breadcrumb;
    },
  });

  console.log('Sentry initialized successfully');
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: {
  id: string;
  email: string;
  name?: string;
  role?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
    role: user.role,
  });
}

/**
 * Clear user context
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for user actions
 */
export function addSentryBreadcrumb(message: string, category: string = 'user', data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture exception with context
 */
export function captureSentryException(error: Error, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('error_context', context);
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture message with level
 */
export function captureSentryMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: any) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('message_context', context);
    }
    Sentry.captureMessage(message, level);
  });
}

/**
 * Performance monitoring helpers
 */
export const SentryPerformance = {
  /**
   * Start a transaction for performance monitoring
   */
  startTransaction(name: string, op: string = 'navigation') {
    return Sentry.startSpan({ name, op }, () => {});
  },
  
  /**
   * Add span to current transaction
   */
  addSpan(name: string, op: string = 'function', _data?: Record<string, unknown>) {
    return Sentry.startSpan({ name, op }, () => {});
  },
  
  /**
   * Finish span
   */
  finishSpan(_span: unknown) {
    // Spans are automatically finished in Sentry v8+
  },
};

export default Sentry;
